import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/contact.css';

function Contact() {
  const { t } = useTranslation();
  const contactRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submissionStatus] = useState('');

  // Lógica de Animação de Entrada
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target); // Animar apenas uma vez
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions,
    );

    const contactElements = contactRef.current?.querySelectorAll(
      '.contact-info, .contact-form',
    );
    contactElements?.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(30px)';
      element.style.transition = 'all 0.6s ease';
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple form validation
    if (formData.name && formData.email && formData.message) {
      const messageText = `Olá, meu nome é ${formData.name} e meu e-mail é ${formData.email}. Gostaria de entrar em contato.
      
      Mensagem:
      ${formData.message}
      
      Telefone para contato (opcional): ${formData.phone}`;

      const whatsappUrl = `https://api.whatsapp.com/send/?phone=5535997649301&text=${encodeURIComponent(messageText)}`;

      window.open(whatsappUrl, '_blank');

      // Limpa o formulário após abrir o WhatsApp
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } else {
      alert('Por favor, preencha todos os campos obrigatórios.');
    }
  };


  return (
    <section id='contact' className='contact about-with-dots' ref={contactRef}>
      <div className='dots-background'></div>
      <div className='contact-container'>
        <div className='section-header'>
          <h2 className='section-title'>{t('contact_main_title')}</h2>
          <p className='section-subtitle'>{t('contact_main_subtitle')}</p>
        </div>

        <div className='contact-content'>
          <div className='contact-info'>
            <h3>{t('contact_info_title')}</h3>
            <p>{t('contact_info_subtitle')}</p>

            <div className='contact-item'>
              <i className='fas fa-map-marker-alt'></i>
              <div>
                <h4>{t('contact_info_address')}</h4>
                <p>
                  <a
                    href='https://www.google.com/maps/search/Rua+Walfrido+Barbosa+da+Fonseca,+254,+Damasco,+Varginha,+MG,+37060-180'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('contact_info_full_address')}{' '}
                  </a>
                </p>
              </div>
            </div>
            <div className='contact-item'>
              <i className='fas fa-phone'></i>
              <div>
                <h4>{t('contact_info_phone')}</h4>
                <p>
                  <a
                    href='https://api.whatsapp.com/send/?phone=5535997649301'
                    target='_blank'
                    rel='noopener noreferrer' 
                  >
                    (35) 99764-9301
                  </a>
                </p>
              </div>
            </div>
            <div className='contact-item'>
              <i className='fas fa-envelope'></i>
              <div>
                <h4>E-mail</h4>
                <p>
                  <a href='mailto:contato@periciaseconsultorias.com.br'>
                    contato@periciaseconsultorias.com.br
                  </a>
                </p>
              </div>
            </div>

            <div className='contact-item'>
              <i className='fab fa-instagram'></i>
              <div>
                <h4>Instagram</h4>
                <p>
                  <a
                    href='https://www.instagram.com/pc.periciaseconsultorias?igsh=MTV2eXR4c3Y1MWVkNw=='
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    @pc_pericias_e_consultorias
                  </a>
                </p>
              </div>
            </div>

            <div className='contact-item'>
              <i className='fab fa-linkedin'></i>
              <div>
                <h4>LinkedIn</h4>
                <p>
                  <a
                    href='https://www.linkedin.com/in/paulo-santos-07698059/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    /paulo-santos
                  </a>
                </p>
              </div>
            </div>
          </div>

          <form className='contact-form' onSubmit={handleSubmit}>
            <h3 className='title-orçamento'>{t('contact_form_title')}</h3>
            <p className='form-required-note'>
              {t('contact_form_required_note')}
            </p>

            {submissionStatus && (
              <div
                className={`submission-status ${
                  submissionStatus.includes(t('contact_submission_success'))
                    ? 'success'
                    : 'error'
                }`}
              >
                {submissionStatus}
              </div>
            )}

            <div className='form-group'>
              <label htmlFor='name'>{t('contact_form_label_name')} *</label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className='form-group'>
              <label htmlFor='email'>{t('contact_form_label_email')} *</label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className='form-group'>
              <label htmlFor='phone'>{t('contact_form_label_phone')}</label>
              <input
                type='tel'
                id='phone'
                name='phone'
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className='form-group'>
              <label htmlFor='message'>
                {t('contact_form_label_message')} *
              </label>
              <textarea
                id='message'
                name='message'
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t('contact_form_placeholder_message')}
                required
              />
            </div>
            <button type='submit' className='submit-btn'>
              <i className='fas fa-paper-plane'></i>
              {t('contact_form_button_send')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
